/**
 * AP-28 — **einen Artikel anlegen: ein leeres Blatt statt eines Zwischendialogs**
 * (Wunsch vom 13.9.2026).
 *
 * Dieselben Abschnitte und dieselbe Anordnung wie im Detail, soweit ein Artikel
 * sie vor dem ersten Speichern haben kann: Stammdaten und Verkauf. Einheiten,
 * Kundenpreise, Einkauf und Bild hängen an einer Artikel-ID — sie folgen nach dem
 * Anlegen im Detail, auf das die Seite direkt weiterleitet.
 *
 * Die Einheit wird nach der Art vorbelegt (AP-28 3.1): Leistung → „h",
 * Gerät → „Tag", sonst „Stk" — soweit es die Einheit gibt.
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { erfuelltRecht, istGueltigeEan, normalisiereEan } from '@symphenia/shared-types'
import { useAuthStore } from '@/lib/auth.store'
import { fehlertext } from '@/lib/fehlertext'
import { articlesApi } from '../api/articles.api'
import { Abschnittskarte, Feld, GewichtFeld, SpeichernKnopf, eingabe, zahlEingabe } from '../components/ArticleFormParts'
import { PriceUnitInput, lesePreiseinheit } from '../components/PriceUnitInput'
import { formatPreis, leseDezimal, zuVieleStellen } from '../lib/article-format'
import type { ArticleDuplicate, ArticleGroup, ArticleKind, ArticleUnit } from '../types/article.types'
import { ARTICLE_KINDS } from '../types/article.types'

const VORBELEGUNG: Record<ArticleKind, string> = { SERVICE: 'h', EQUIPMENT: 'Tag', MATERIAL: 'Stk', OTHER: 'Stk' }

const LEER = {
  articleNumber: '',
  ean: '',
  name: '',
  kind: 'MATERIAL' as ArticleKind,
  unitId: '',
  groupId: '',
  documentText: '',
  internalNote: '',
  weightPerUnit: '',
  isSalesArticle: true,
  isPurchaseArticle: false,
  salesPrice: '',
  priceUnit: '1',
  taxRate: '',
}
type Form = typeof LEER

export default function ArticleNewPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const darfListen = erfuelltRecht('articles.list.manage', user?.permissions)

  const [einheiten, setEinheiten] = useState<ArticleUnit[]>([])
  const [gruppen, setGruppen] = useState<ArticleGroup[]>([])
  /** Die nächste Nummer nach dem eingestellten Aufbau (Nachtrag N-2) — nur als Platzhalter. */
  const [naechsteNummer, setNaechsteNummer] = useState<string | null>(null)
  const [form, setForm] = useState<Form>(LEER)
  const [fehler, setFehler] = useState<string | null>(null)
  const [speichert, setSpeichert] = useState(false)
  // N-21: Gibt es den schon? — gefragt, sobald Bezeichnung oder EAN stehen, und
  // noch einmal beim Speichern (`409`), falls zwei Leute gleichzeitig tippen.
  const [kandidaten, setKandidaten] = useState<ArticleDuplicate[]>([])
  const [pruefeDubletten, setPruefeDubletten] = useState(false)
  const dublettenTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const setze = (patch: Partial<Form>) => setForm((f) => ({ ...f, ...patch }))

  useEffect(() => {
    if (dublettenTimer.current) clearTimeout(dublettenTimer.current)
    const name = form.name.trim()
    const ean = normalisiereEan(form.ean)
    // Eine unfertige EAN ist noch kein Merkmal — erst die gültige wird verglichen.
    const eanMerkmal = istGueltigeEan(ean) ? ean : ''
    if (name.length < 3 && !eanMerkmal) {
      setKandidaten([])
      setPruefeDubletten(false)
      return
    }
    setPruefeDubletten(true)
    dublettenTimer.current = setTimeout(async () => {
      try {
        const { data } = await articlesApi.duplicates({ name: name || undefined, ean: eanMerkmal || undefined })
        setKandidaten(data.data)
      } catch {
        // Ein Hinweis, keine Bedingung — das `409` beim Speichern fängt den Fall.
        setKandidaten([])
      } finally {
        setPruefeDubletten(false)
      }
    }, 500)
    return () => {
      if (dublettenTimer.current) clearTimeout(dublettenTimer.current)
    }
  }, [form.name, form.ean])

  const uebernehmeEinheiten = (alle: ArticleUnit[]) => {
    const aktive = alle.filter((e) => e.isActive)
    setEinheiten(aktive)
    setForm((f) => (f.unitId ? f : { ...f, unitId: vorbelegteEinheit(aktive, f.kind) }))
  }

  useEffect(() => {
    articlesApi
      .units()
      .then(({ data }) => uebernehmeEinheiten(data.data))
      .catch((err) => setFehler(fehlertext(err, t)))
    articlesApi
      .groups()
      .then(({ data }) => setGruppen(data.data.filter((g) => g.isActive)))
      .catch(() => undefined)
    // Ein Fehler hier ist still: Der Platzhalter fällt auf den bisherigen Text zurück.
    articlesApi
      .settings()
      .then(({ data }) => setNaechsteNummer(data.data.nextNumber))
      .catch(() => setNaechsteNummer(null))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const artWaehlen = (kind: ArticleKind) => setze({ kind, unitId: vorbelegteEinheit(einheiten, kind) || form.unitId })

  const vorschlaege = async () => {
    try {
      const { data } = await articlesApi.seedUnits()
      uebernehmeEinheiten(data.data)
    } catch (err) {
      setFehler(fehlertext(err, t))
    }
  }

  const einheitKuerzel = einheiten.find((u) => u.id === form.unitId)?.code ?? ''
  const preis = leseDezimal(form.salesPrice)
  const preiseinheit = lesePreiseinheit(form.priceUnit)
  const vorschau =
    preis !== null && !Number.isNaN(preis) && preiseinheit !== null ? formatPreis(preis, preiseinheit, einheitKuerzel) : '—'

  const anlegen = async (allowDuplicate: boolean) => {
    setFehler(null)
    if (!form.isSalesArticle && !form.isPurchaseArticle) return setFehler(t('articles.roleRequired'))

    const gewicht = leseDezimal(form.weightPerUnit)
    if (gewicht !== null && (Number.isNaN(gewicht) || gewicht < 0 || zuVieleStellen(gewicht, 4))) {
      return setFehler(t('articles.weightInvalid'))
    }
    const ean = normalisiereEan(form.ean)
    if (ean && !istGueltigeEan(ean)) return setFehler(t('articles.eanInvalid'))
    if (form.isSalesArticle) {
      if (preis !== null && (Number.isNaN(preis) || preis < 0)) return setFehler(t('articles.priceInvalid'))
      if (preis !== null && zuVieleStellen(preis, 2)) return setFehler(t('articles.priceDecimals'))
      if (preiseinheit === null) return setFehler(t('articles.priceUnitInvalid'))
    }
    const satz = leseDezimal(form.taxRate)
    if (form.isSalesArticle && satz !== null && (Number.isNaN(satz) || satz < 0 || satz > 100 || zuVieleStellen(satz, 2))) {
      return setFehler(t('articles.taxRateInvalid'))
    }

    setSpeichert(true)
    try {
      const { data } = await articlesApi.create({
        name: form.name.trim(),
        unitId: form.unitId,
        kind: form.kind,
        isSalesArticle: form.isSalesArticle,
        isPurchaseArticle: form.isPurchaseArticle,
        ...(allowDuplicate && { allowDuplicate: true }),
        ...(form.articleNumber.trim() && { articleNumber: form.articleNumber.trim() }),
        ...(form.groupId && { groupId: form.groupId }),
        ...(form.documentText.trim() && { documentText: form.documentText }),
        ...(form.internalNote.trim() && { internalNote: form.internalNote }),
        ...(gewicht !== null && { weightPerUnit: gewicht }),
        ...(ean && { ean }),
        ...(form.isSalesArticle && {
          ...(preis !== null && { salesPrice: preis }),
          ...(preiseinheit !== null && { priceUnit: preiseinheit }),
          ...(satz !== null && { taxRate: satz }),
        }),
      })
      // `replace`: Zurück führt zur Liste, nicht auf ein leeres Blatt, das den Artikel doppelt anlegte.
      navigate(`/articles/${data.id}`, { replace: true })
    } catch (err) {
      const antwort = (err as { response?: { status?: number; data?: { message?: unknown } } })?.response
      const inhalt = antwort?.data?.message
      // Das `409` der Dublettenprüfung trägt die Kandidaten im Körper — sie zeigen, nicht die Meldung.
      if (antwort?.status === 409 && typeof inhalt === 'object' && inhalt !== null && 'candidates' in inhalt) {
        setKandidaten((inhalt as { candidates: ArticleDuplicate[] }).candidates)
      } else {
        setFehler(fehlertext(err, t))
      }
    } finally {
      setSpeichert(false)
    }
  }

  const absenden = async (e: React.FormEvent) => {
    e.preventDefault()
    await anlegen(false)
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="space-y-3">
        <button type="button" onClick={() => navigate('/articles')} className="text-sm text-muted-foreground hover:text-foreground">
          ‹ {t('articles.title')}
        </button>
        <h1 className="text-3xl font-bold text-foreground">{t('articles.create')}</h1>
      </div>

      {fehler && <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>}

      <form onSubmit={absenden} className="max-w-5xl space-y-6">
        <Abschnittskarte
          titel={t('articles.sections.base')}
          aktion={
            <div className="flex items-center gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={form.isSalesArticle} onChange={(e) => setze({ isSalesArticle: e.target.checked })} />
                {t('articles.role.sales')}
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.isPurchaseArticle}
                  onChange={(e) => setze({ isPurchaseArticle: e.target.checked })}
                />
                {t('articles.role.purchase')}
              </label>
            </div>
          }
        >
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-3">
              <Feld label={t('articles.fields.articleNumber')}>
                <input
                  maxLength={40}
                  placeholder={naechsteNummer ? t('articles.numberAutoNext', { number: naechsteNummer }) : t('articles.numberAuto')}
                  value={form.articleNumber}
                  onChange={(e) => setze({ articleNumber: e.target.value })}
                  className={`${eingabe} font-mono`}
                />
              </Feld>
              <Feld label={t('articles.fields.ean')}>
                <input
                  inputMode="numeric"
                  maxLength={20}
                  value={form.ean}
                  onChange={(e) => setze({ ean: e.target.value })}
                  className={`${eingabe} font-mono tabular-nums`}
                />
              </Feld>
            </div>

            <Feld label={`${t('articles.fields.name')} *`}>
              <input
                autoFocus
                required
                maxLength={200}
                value={form.name}
                onChange={(e) => setze({ name: e.target.value })}
                className={`${eingabe} text-lg font-semibold`}
              />
            </Feld>

            {/* Gibt es den schon? (N-21) */}
            {(pruefeDubletten || kandidaten.length > 0) && (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm dark:border-amber-800 dark:bg-amber-950/40">
                {pruefeDubletten && kandidaten.length === 0 ? (
                  <span className="text-muted-foreground">{t('articles.duplicates.checking')}</span>
                ) : (
                  <>
                    <p className="font-medium text-foreground">{t('articles.duplicates.title')}</p>
                    <p className="mt-0.5 text-muted-foreground">{t('articles.duplicates.intro')}</p>
                    <ul className="mt-2 space-y-1">
                      {kandidaten.map((k) => (
                        <li key={k.id} className="flex items-center justify-between gap-2">
                          <span className="text-foreground">
                            <span className="font-mono text-xs text-muted-foreground">{k.articleNumber}</span> {k.name}
                            {!k.isActive && (
                              <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs">{t('articles.status.inactive')}</span>
                            )}
                            <span className="ml-2 text-xs text-muted-foreground">
                              ({k.matchedOn.map((m) => t(`articles.duplicates.matchedOn.${m}`)).join(', ')})
                            </span>
                          </span>
                          <button
                            type="button"
                            onClick={() => navigate(`/articles/${k.id}`)}
                            className="shrink-0 text-xs font-medium text-primary hover:underline"
                          >
                            {t('articles.duplicates.openExisting')}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-3">
              <Feld label={t('articles.fields.kind')}>
                <select value={form.kind} onChange={(e) => artWaehlen(e.target.value as ArticleKind)} className={eingabe}>
                  {ARTICLE_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {t(`articles.kinds.${k}`)}
                    </option>
                  ))}
                </select>
              </Feld>
              <Feld label={t('articles.fields.group')}>
                <select value={form.groupId} onChange={(e) => setze({ groupId: e.target.value })} className={eingabe}>
                  <option value="">—</option>
                  {gruppen.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              </Feld>
              <Feld label={`${t('articles.fields.unit')} *`}>
                {einheiten.length === 0 ? (
                  <div className="rounded-md border border-dashed border-border px-3 py-2 text-sm text-muted-foreground">
                    {t('articles.hints.noUnits')}
                    {darfListen && (
                      <button type="button" onClick={() => void vorschlaege()} className="ml-2 text-primary hover:underline">
                        {t('articles.actions.seedUnits')}
                      </button>
                    )}
                  </div>
                ) : (
                  <select required value={form.unitId} onChange={(e) => setze({ unitId: e.target.value })} className={eingabe}>
                    <option value="">—</option>
                    {einheiten.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.code} — {u.name}
                      </option>
                    ))}
                  </select>
                )}
              </Feld>
            </div>

            <Feld label={t('articles.fields.weightPerUnit')} hinweis={t('articles.hints.weight')}>
              <GewichtFeld value={form.weightPerUnit} onChange={(text) => setze({ weightPerUnit: text })} einheit={einheitKuerzel} />
            </Feld>

            <Feld label={t('articles.fields.documentText')} hinweis={t('articles.hints.documentText')}>
              <textarea
                rows={3}
                maxLength={4000}
                value={form.documentText}
                onChange={(e) => setze({ documentText: e.target.value })}
                className={eingabe}
              />
            </Feld>
            <Feld label={t('articles.fields.internalNote')} hinweis={t('articles.hints.internalNote')}>
              <textarea
                rows={2}
                maxLength={4000}
                value={form.internalNote}
                onChange={(e) => setze({ internalNote: e.target.value })}
                className={eingabe}
              />
            </Feld>
          </div>
        </Abschnittskarte>

        {form.isSalesArticle && (
          <Abschnittskarte titel={t('articles.sections.sales')}>
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-3">
                <Feld label={t('articles.fields.salesPriceLong')} hinweis={t('articles.hints.salesPrice')}>
                  <input
                    inputMode="decimal"
                    value={form.salesPrice}
                    onChange={(e) => setze({ salesPrice: e.target.value })}
                    className={zahlEingabe}
                  />
                </Feld>
                <Feld label={t('articles.fields.priceUnit')} hinweis={`→ ${vorschau}`}>
                  <PriceUnitInput value={form.priceUnit} onChange={(text) => setze({ priceUnit: text })} className={zahlEingabe} />
                </Feld>
                <Feld label={t('articles.fields.taxRateLong')} hinweis={t('articles.hints.taxRate')}>
                  <input inputMode="decimal" value={form.taxRate} onChange={(e) => setze({ taxRate: e.target.value })} className={zahlEingabe} />
                </Feld>
              </div>
              <p className="text-xs text-muted-foreground">{t('articles.hints.priceUnit')}</p>
            </div>
          </Abschnittskarte>
        )}

        <p className="rounded-lg border border-dashed border-border px-4 py-3 text-sm text-muted-foreground">
          {t('articles.hints.afterCreate')}
        </p>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => navigate('/articles')}
            className="rounded-md border border-input px-3 py-1.5 text-sm text-foreground hover:bg-muted/50"
          >
            {t('common.cancel')}
          </button>
          {kandidaten.length > 0 && (
            <button
              type="button"
              onClick={() => void anlegen(true)}
              disabled={speichert || !form.unitId || !form.name.trim()}
              className="rounded-md border border-amber-400 px-3 py-1.5 text-sm font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50 dark:text-amber-200 dark:hover:bg-amber-900/40"
            >
              {t('articles.duplicates.createAnyway')}
            </button>
          )}
          <SpeichernKnopf disabled={speichert || !form.unitId || !form.name.trim()} label={t('articles.create')} />
        </div>
      </form>
    </div>
  )
}

function vorbelegteEinheit(einheiten: ArticleUnit[], kind: ArticleKind): string {
  return einheiten.find((u) => u.code === VORBELEGUNG[kind])?.id ?? einheiten[0]?.id ?? ''
}
